import { useEffect, type RefObject } from "react";

/**
 * Fades in every `[data-reveal]` element inside `rootRef` the first time it
 * scrolls into view. Visibility is exposed as `data-visible="true"` so React's
 * own className handling never fights with it.
 */
export const useScrollReveal = (rootRef: RefObject<HTMLElement | null>): void => {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const items = Array.from(
      root.querySelectorAll<HTMLElement>("[data-reveal]"),
    );
    // Stagger neighbours slightly; the delay only applies to the fade, not hover.
    items.forEach((el, index) =>
      el.style.setProperty("--al-delay", `${(index % 6) * 60}ms`),
    );

    if (typeof IntersectionObserver === "undefined") {
      items.forEach((el) => {
        el.dataset.visible = "true";
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          (entry.target as HTMLElement).dataset.visible = "true";
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15 },
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [rootRef]);
};
